import { useSyncState } from '../hooks/useSyncState'

const STATUS_META = {
  synced: { label: 'SYNC', className: 'text-sync border-sync/25' },
  pending: { label: 'PEND', className: 'text-pending border-pending/25' },
  syncing: { label: 'SYNC…', className: 'text-pending border-pending/25 animate-pulse' },
  conflict: { label: 'CONF', className: 'text-conflict border-conflict/30' },
  offline: { label: 'OFF', className: 'text-zinc-500 border-edge' },
  error: { label: 'RETRY', className: 'text-conflict border-conflict/30' },
} as const

export type BadgeStatus = keyof typeof STATUS_META

export function statusFor(pointId: string, phase: string, pending: string[], conflicts: string[]): BadgeStatus {
  if (conflicts.includes(pointId)) return 'conflict'
  if (pending.includes(pointId)) return phase === 'syncing' ? 'syncing' : 'pending'
  if (phase === 'offline') return 'offline'
  if (phase === 'error') return 'error'
  return 'synced'
}

/** Monochrome sync chip: emerald synced, amber pending, red conflict. */
export function SyncBadge({ pointId }: { pointId: string }) {
  const sync = useSyncState()
  const status = statusFor(pointId, sync.phase, sync.pending, sync.conflicts)
  const meta = STATUS_META[status]
  return (
    <span
      className={`mono rounded border bg-ink-800 px-1.5 py-0.5 text-[10px] font-medium tracking-[0.08em] ${meta.className}`}
      title={`Sync status: ${status}`}
    >
      {meta.label}
    </span>
  )
}

export function PhaseBadge() {
  const sync = useSyncState()
  const status: BadgeStatus =
    sync.conflicts.length > 0
      ? 'conflict'
      : sync.pending.length > 0
        ? sync.phase === 'syncing'
          ? 'syncing'
          : 'pending'
        : sync.phase === 'offline'
          ? 'offline'
          : sync.phase === 'error'
            ? 'error'
            : 'synced'
  const meta = STATUS_META[status]
  const detail = sync.pending.length > 0 ? ` · ${sync.pending.length} queued` : sync.lastSyncAt !== null ? ` · ${new Date(sync.lastSyncAt).toLocaleTimeString()}` : ''
  return (
    <span className={`mono rounded border bg-ink-800 px-2 py-1 text-[10px] tracking-[0.08em] ${meta.className}`}>
      {meta.label}
      {detail}
    </span>
  )
}
